import { useCallback, useEffect, useRef, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'

type UseLeaveReviewCartPromptReturn = {
  hasUnsavedChanges: boolean
  markAsChanged: () => void
  clearChanges: () => void
  isPromptOpen: boolean
  continueNavigation: () => void
  cancelNavigation: () => void
}

type UseLeaveReviewCartPromptOptions = {
  /** Called when user confirms leaving - use to clear data before navigating (e.g. when destination is cancel) */
  onConfirmLeave?: (destinationPath: string) => void
}

const normalizePath = (path: string) => (path || '/').replace(/\/$/, '') || '/'

export const useLeaveReviewCartPrompt = (
  options?: UseLeaveReviewCartPromptOptions
): UseLeaveReviewCartPromptReturn => {
  const { onConfirmLeave } = options ?? {}
  const history = useHistory()
  const location = useLocation()
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  const [isPromptOpen, setIsPromptOpen] = useState(false)
  const continueNavigationRef = useRef<() => void>(() => {})

  const markAsChanged = useCallback(() => {
    setHasUnsavedChanges(true)
  }, [])

  const clearChanges = useCallback(() => {
    setHasUnsavedChanges(false)
  }, [])

  const continueNavigation = useCallback(() => {
    setIsPromptOpen(false)
    setHasUnsavedChanges(false)
    continueNavigationRef.current()
  }, [])

  const cancelNavigation = useCallback(() => {
    setIsPromptOpen(false)
  }, [])

  useEffect(() => {
    if (!hasUnsavedChanges) {
      return
    }

    const unblock = history.block((nextLocation) => {
      const currentPath = normalizePath(location.pathname)
      const nextPath = normalizePath(nextLocation.pathname)

      // Allow navigation to checkout/shop - these are intentional "save" actions (Proceed to Checkout, Add to Cart)
      const isSaveDestination = nextPath === '/checkout' || nextPath === '/shop'
      if (isSaveDestination) {
        setHasUnsavedChanges(false)
        return undefined
      }

      // Block navigation to a different page - show leave confirmation modal
      if (currentPath !== nextPath) {
        setIsPromptOpen(true)
        continueNavigationRef.current = () => {
          onConfirmLeave?.(nextLocation.pathname)
          unblock()
          history.push(nextLocation.pathname, nextLocation.state)
        }
        return false
      }

      return undefined
    })

    return () => {
      unblock()
    }
  }, [history, location.pathname, hasUnsavedChanges, onConfirmLeave])

  // Handle browser back/forward button and page close
  useEffect(() => {
    if (!hasUnsavedChanges) {
      return
    }

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault()
      e.returnValue = ''
      return ''
    }

    window.addEventListener('beforeunload', handleBeforeUnload)

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [hasUnsavedChanges])

  return {
    hasUnsavedChanges,
    markAsChanged,
    clearChanges,
    isPromptOpen,
    continueNavigation,
    cancelNavigation,
  }
}
